import { CRM_COPY } from './copy';

/** "Oct 17" in the viewer's locale; the date a lead was last sent to a CRM. */
function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Every user-facing string the unified CRM grid adds (port plan step 6: the
 * Clean, Match and Send tabs reuse the Leads grid). Wherever the grid says
 * what a legacy view already said, it uses `CRM_COPY` itself rather than a
 * copy of it, so the two modes can't drift apart; this object holds only what
 * is new.
 */
export const CRM_GRID_COPY = {
  /** The status column the lenses add after Name. */
  statusTitle: 'In your CRM',
  /** Match's owner column (the CRM record owner, not VendoIQ's Assigned to). */
  ownerTitle: 'CRM owner',
  kind: {
    checking: 'Looking up…',
    unavailable: 'Couldn’t look up',
    unchecked: 'Not checked yet',
    no_email: CRM_COPY.enrichSeam,
    held: 'Held by lead rules',
    ambiguous: CRM_COPY.reasonMultipleCrmRecords,
    fields_differ: (n: number): string => (n === 1 ? '1 field differs' : `${n} fields differ`),
    in_crm: 'Already in CRM',
    new: 'New record',
    sent_unchanged: 'Sent, nothing new',
  },
  /** What the status pills' "?" says, where `CRM_COPY.help` has no entry. */
  help: {
    /**
     * Refresh's "?" in grid mode. Same promise about traffic as
     * `CRM_COPY.help.refresh` (the live read still waits for Match or Send,
     * D8), but the numbers come from different places now (D9): Clean from
     * the check plus the lead rules, Match and Send from the live read.
     */
    refresh: {
      label: CRM_COPY.help.refresh.label,
      text: 'When you open an event, we check your CRM only for leads we haven’t looked up before, or whose email changed. That check and your lead rules give the Clean count. The first time you open Match or Send, we look up your leads in your CRM, except leads already sent with nothing new. That takes a moment, and it fills in the Match and Send counts. Press Refresh to check again after you change something in your CRM. Nothing is written to your CRM until you press Send.',
    },
    /**
     * The status column's one glossary (legacy Match explained New and
     * Already in your CRM once per column; the grid has one column for both).
     */
    statuses: {
      label: 'What the “In your CRM” statuses mean',
      title: 'What each status means',
      lines: [
        `New record: ${CRM_COPY.help.matchNewGroup.text}`,
        `Already in CRM: ${CRM_COPY.help.matchAlreadyGroup.text}`,
        'Fields differ: This person is in your CRM, but some fields don’t match what we captured. On the Match tab, click the status to choose which value to keep.',
        `Multiple CRM records: ${CRM_COPY.help.matchAmbiguous.text}`,
        'No email: We can’t check your CRM for this lead by email. It can still be sent, unless your lead rules hold it back. Add an email on the Clean tab.',
        'Held by lead rules: Your company’s lead rules hold this lead back until it has what they require.',
        'Sent, nothing new: Already sent, with no changed details and no new notes or follow-ups since.',
      ],
    },
    /** Clean's hint in grid mode, where Clean also lists rule-held leads. */
    tabClean: {
      label: CRM_COPY.help.tabClean.label,
      text: 'Leads with no email, and leads your company’s lead rules hold back. A lead with no email can still be sent, but we can’t check first whether it’s already in your CRM. Add an email to fix that, or leave it for now. A held lead isn’t sent until it has what your lead rules require.',
    },
    unchecked: {
      label: 'Why this lead isn’t checked yet',
      text: 'This lead was captured after your CRM was last checked. Press Refresh to check it.',
    },
    unavailable: {
      label: 'Why this lead couldn’t be looked up',
      text: 'We couldn’t read your CRM just now. Press Retry above the grid to try again.',
    },
    /** Match / Send's "No email": the live read agrees, so it isn't sent (`isReady`). */
    noEmailNotSent: {
      label: 'Why this lead isn’t part of this send',
      text: 'No email yet, so this lead isn’t part of this send. Add one on the Clean tab.',
    },
    held: {
      label: 'What “Held by lead rules” means',
      text: 'Your company’s lead rules hold this lead back, so it can’t be sent until it has what they require. The reason shows under each lead’s status. Fix it, then press Refresh. You can see the rules in Company settings, under Lead rules.',
    },
    fieldsDiffer: {
      label: 'What “fields differ” means',
      text: 'This lead is in your CRM, but some fields don’t match what we captured. On the Match tab, click the status to compare them side by side and choose which value to keep. If you don’t choose, the “When a field differs” setting decides.',
    },
    sentUnchanged: {
      label: 'What “Sent, nothing new” means',
      text: 'Already sent, with no changed details and no new notes or follow-ups since, so this send leaves it out. To send it again anyway, press “Include them” above the grid.',
    },
    /** Send's "Already sent (N)": the leads it lists but leaves out. */
    alreadySent: {
      label: 'What “Already sent” means',
      text: 'Leads sent to your CRM before, with no changed details and no new notes or follow-ups since, so this send leaves them out. They stay listed so you can see what is already in your CRM, with the date each one was last sent. To send them again anyway, press “Include them”.',
    },
  },
  /** "Sent Oct 17" under a status: when the lead was last sent (lead row's `crmSentAt`). */
  sentOn: (iso: string): string => `Sent ${shortDate(iso)}`.trim(),
  /** Under a no-email lead whose saved email didn't let the CRM check it. */
  clean: {
    /** Clean's toolbar, beside the legacy "Needs email (N)" header. */
    heldHeader: 'Held by lead rules',
    empty: 'Nothing to fix here: every lead has an email, and your lead rules hold none back.',
    /** Toast once a re-check finds the fixed email usable. */
    movedOn: (name: string): string => `Saved. We can now check ${name} against your CRM.`,
  },
  match: {
    filterLabel: 'Show',
    filters: {
      decide: 'Needs your decision',
      all: 'All',
      in_crm: CRM_COPY.alreadyInCrm,
      new: CRM_COPY.newHeader,
      no_email: CRM_COPY.enrichSeam,
    },
    empty: 'No leads in this view.',
    /** The one-line merge policy control. Its options are `CRM_COPY.policyFillBlanks` / `policyOverwrite`. */
    policyLabel: 'When a field differs',
    /** A disabled bulk-owner checkbox: the record keeps the owner it has. */
    ownerNotNeeded: 'Keeps the owner it already has in your CRM',
    ownerNotYet: 'Still looking this lead up in your CRM',
  },
  send: {
    button: (n: number, crm: string): string => `Send ${n} to ${crm || 'your CRM'}`,
    /** Results line: `Results (N): 3 created · 1 failed`. */
    resultsLine: (parts: string[]): string => parts.join(' · '),
    clearResults: 'Clear results',
    readOnlyReason: CRM_COPY.help.readOnly.text,
    checkingReason: CRM_COPY.previewLoadingLine,
    notInPreview: 'Not part of this send',
    /** Send's count of the leads already sent with nothing new: listed, never ticked. */
    alreadySentHeader: 'Already sent',
    /** An already-sent lead's disabled box. */
    alreadySentReason: 'Already sent, with nothing new since',
    /** Re-reads them under scope 'all', so they can be sent again (`showAllPreview`). */
    includeAlreadySent: 'Include them',
    /** Send waits for the grid to show whom it sends. */
    rowsLoading: 'Loading the leads…',
    rowsFailed: 'Couldn’t load the leads',
    /** The Retry beside that reason: its accessible name. */
    retryRowsLabel: 'Retry loading the leads',
  },
  /** Match / Send, when the leads changed since the live read (with Refresh). */
  previewStale: 'Leads changed since the CRM check',
  /** The grid's own line when the event's leads didn't load (with Retry). */
  rowsFailed: 'Couldn’t load the leads',
  /**
   * The event rail's badge in grid mode (port plan D9): no-email, rule-held,
   * needs-a-decision and unresolved-owner leads, each person counted once.
   * The last two are only known once Match or Send has been opened (the live
   * read is lazy, D8), so before then the sentence names only the first two.
   */
  badgeTooltip: (n: number, includesLiveRead: boolean): string => {
    const lead = `${n} ${n === 1 ? 'lead is' : 'leads are'} worth a look`;
    return includesLiveRead
      ? `${lead}: no email, held back by your lead rules, a field that differs from your CRM, or a CRM owner to check.`
      : `${lead}: no email, or held back by your lead rules.`;
  },
} as const;
